import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Modal } from "../utils/Ui/Modal";
import { search } from "../utils/api/helpers/utils";
import { S3URL } from "../data/globalConstants";
import { useGuildPath } from "../utils/Guild/useGuild";

export default function SearchShows({ displaySearch, setDisplaySearch }) {
  const inputRef = useRef(null);
  const listboxRef = useRef(null);
  const debounceRef = useRef(null);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const guildPath = useGuildPath();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query?.trim()) {
      setResults([]);
      setActiveIndex(-1);
      setIsLoading(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      setIsLoading(true);

      search(query.trim())
        .then((res) => {
          setResults(Array.isArray(res?.data) ? res.data : []);
          // console.log(res.data);
          setActiveIndex(-1);
        })
        .catch(() => {
          setResults([]);
          setActiveIndex(-1);
        })
        .finally(() => setIsLoading(false));
    }, 250);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const getResultPath = (item) => {
    const rawGenreSlug = item?.genres?.[0]?.slug;
    const showSlug = item?.slug;

    if (!rawGenreSlug || !showSlug) return "#";

    const genreSlug = String(rawGenreSlug).toLowerCase();

    let mappedGenreSlug = rawGenreSlug;

    if (genreSlug.includes("movie") || genreSlug.includes("limited")) {
      mappedGenreSlug = "limited-series-movie";
    } else if (genreSlug.includes("documentary")) {
      mappedGenreSlug = "documentary-series-or-special";
    } else if (genreSlug.includes("variety")) {
      mappedGenreSlug = "variety-programs";
    } else if (genreSlug.includes("nonfiction")) {
      mappedGenreSlug = "nonfiction-series";
    }

    return guildPath(`/${mappedGenreSlug}/${showSlug}`);
  };

  const handleKeyDown = (e) => {
    if (!results.length) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
        break;

      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
        break;

      case "Enter":
        if (activeIndex >= 0) {
          e.preventDefault();
          const el = document.getElementById(`search-option-${activeIndex}`);
          el?.click();
          setDisplaySearch(false);
        }
        break;

      case "Escape":
        setResults([]);
        setActiveIndex(-1);
        break;

      default:
        break;
    }
  };

  const clearSearch = () => {
    setQuery("");
    setResults([]);
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  return (
    <Modal
      scrollable={true}
      isOpen={displaySearch}
      onClose={() => setDisplaySearch(false)}
      title="Search Netflix Series FYC"
      size="full"
      contentScrollRef={null}
    >
      <div role="search" aria-label="Site search" className="search-container">
        <div className="search-input-container">
          <label htmlFor="search-input" className="sr-only">
            Search
          </label>

          <input
            id="search-input"
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-autocomplete="list"
            aria-controls="search-results"
            aria-expanded={results.length > 0}
            aria-activedescendant={
              activeIndex >= 0 ? `search-option-${activeIndex}` : undefined
            }
            placeholder="Search by Title, Cast, or Keyword"
            className="search-input"
          />

          {query && (
            <button
              type="button"
              className="search-clear-button"
              onClick={clearSearch}
              aria-label="Clear search"
              onMouseDown={(e) => e.preventDefault()}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M6 6L18 18M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
        </div>

        <div
          id="search-results"
          ref={listboxRef}
          role="listbox"
          aria-live="polite"
          className="search-results"
        >
          {isLoading ? (
            <div className="search-result" role="status">
              Searching...
            </div>
          ) : !isLoading && query.trim() && results.length === 0 ? (
            <div className="search-result" role="status">
              No results
            </div>
          ) : (
            results.map((item, index) => (
              <div key={`${item.slug}-${index}`} className={"search-result"}>
                <a
                  id={`search-option-${index}`}
                  href={getResultPath(item)}
                  role="option"
                  aria-selected={index === activeIndex}
                  tabIndex={-1}
                  className={`search-result-link ${
                    index === activeIndex ? "active" : ""
                  }`}
                  // onMouseDown={(e) => e.preventDefault()}
                  // onClick={() => setDisplaySearch(false)}
                >
                  <img src={S3URL + item?.keyArt} alt={item?.title} />
                  <span className={"search-result-link-title"}>
                    {item?.title}
                  </span>
                  <span className={"search-result-link-genre"}>
                    {item?.genres?.[0]?.name}
                  </span>
                </a>
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}
